import { z } from 'zod';

/// When VITE_API_URL is unset (the normal prod case — same-origin
/// behind nginx), we resolve to `${window.location.origin}/api` at
/// runtime so the same build works on every host without rebuilding.
/// Dev with `vite dev` sets VITE_API_URL=http://localhost:4000/api in
/// apps/web/.env so the SPA can talk to the API on a different port.
const resolveDefaultApiUrl = (): string => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api`;
  }
  // Build-time / test fallback. The runtime branch above wins in the
  // browser, so this only matters for SSR-style execution (none today).
  return 'http://localhost:4000/api';
};

export const envSchema = z.object({
  VITE_API_URL: z.string().url().optional(),
  VITE_DEFAULT_LOCALE: z.enum(['ar', 'en']).default('ar'),
});

export type RawEnv = z.infer<typeof envSchema>;
export interface Env {
  VITE_API_URL: string;
  VITE_DEFAULT_LOCALE: 'ar' | 'en';
}

export function parseEnv(source: Record<string, unknown>): Env {
  const result = envSchema.safeParse(source);
  if (!result.success) {
    const formatted = result.error.errors
      .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
      .join('\n');
    throw new Error(`Invalid frontend environment configuration:\n${formatted}`);
  }
  return {
    VITE_API_URL: result.data.VITE_API_URL ?? resolveDefaultApiUrl(),
    VITE_DEFAULT_LOCALE: result.data.VITE_DEFAULT_LOCALE,
  };
}

export const env: Env = parseEnv(import.meta.env);
